import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Dialog from "../../dialog/Dialog";
import TinyEditor from "../../Editor/TinyEditor";
import {
  downloadPackageItineraryAdmin,
  getGeneralInfo,
  pricingMaster,
} from "./packageService";

function isPackageApproved(value) {
  return value === true || value === 1 || value === "1";
}

function createCustomDay() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: "",
    description: "",
  };
}

function hasEditorContent(html = "") {
  return Boolean(html.replace(/<[^>]*>/g, "").trim());
}

const PackageItineraryDownload = ({ packageId, packageName }) => {
  const [open, setOpen] = useState(false);
  const [itineraryFormat, setItineraryFormat] = useState("daywise");
  const [startDate, setStartDate] = useState("");
  const [stayCategoryId, setStayCategoryId] = useState("");
  const [customPrefixDays, setCustomPrefixDays] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  const { data: generalInfoResponse } = useQuery({
    queryKey: ["generalInfo", packageId],
    queryFn: () => getGeneralInfo(packageId),
    enabled: Boolean(packageId),
  });

  const { data: stayCategoriesResponse, isLoading: stayCategoriesLoading } =
    useQuery({
      queryKey: ["packageStayCategories", packageId],
      queryFn: () => pricingMaster(packageId),
      enabled: Boolean(packageId) && open,
    });

  const isApproved = isPackageApproved(generalInfoResponse?.data?.is_approved);

  const stayCategories = useMemo(() => {
    const rows = stayCategoriesResponse?.data || [];
    return rows
      .map((row) => ({
        id: row.stay_category_id,
        label: row.stay_category?.title || `Category ${row.stay_category_id}`,
      }))
      .filter((row) => row.id);
  }, [stayCategoriesResponse]);

  const canDownload =
    stayCategoryId &&
    (itineraryFormat === "daywise" ||
      (itineraryFormat === "datewise" && startDate));

  const itineraryStartDay = customPrefixDays.length + 1;

  const resetDialog = () => {
    setItineraryFormat("daywise");
    setStartDate("");
    setStayCategoryId("");
    setCustomPrefixDays([]);
    setError("");
  };

  const handleClose = () => {
    if (isDownloading) return;
    setOpen(false);
    resetDialog();
  };

  const handleDownload = async () => {
    if (!canDownload) return;

    setIsDownloading(true);
    setError("");

    try {
      const daysToSend = customPrefixDays.filter(
        (day) => day.title.trim() || hasEditorContent(day.description)
      );

      await downloadPackageItineraryAdmin({
        packageId,
        packageName: packageName || generalInfoResponse?.data?.name || "package",
        stayCategoryId,
        daywise: itineraryFormat === "daywise",
        startDate: itineraryFormat === "datewise" ? startDate : null,
        customPrefixDays: daysToSend,
      });
      handleClose();
    } catch (downloadError) {
      setError(downloadError.message || "Failed to download itinerary");
    } finally {
      setIsDownloading(false);
    }
  };

  const addCustomDay = () => {
    setCustomPrefixDays((prev) => [...prev, createCustomDay()]);
  };

  const updateCustomDay = (id, field, value) => {
    setCustomPrefixDays((prev) =>
      prev.map((day) => (day.id === id ? { ...day, [field]: value } : day))
    );
  };

  const removeCustomDay = (id) => {
    setCustomPrefixDays((prev) => prev.filter((day) => day.id !== id));
  };

  if (!packageId || !isApproved) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError("");
          setOpen(true);
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
      >
        <i className="fi fi-rr-download relative top-0.5 text-primary-600" />
        <span className="hidden sm:inline">Download itinerary</span>
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        title="Download itinerary"
        maxWidth="5xl"
        maxHeight="90vh"
        primaryButtonText={isDownloading ? "Downloading..." : "Download PDF"}
        primaryButtonAction={handleDownload}
        isLoading={isDownloading}
        isPrimaryDisabled={!canDownload || isDownloading}
        closeButtonAction={handleClose}
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Choose stay category and format, then optionally add custom prefix days before the package itinerary.
          </p>

          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-800">
                Stay category
              </label>
              {stayCategoriesLoading ? (
                <p className="text-sm text-gray-500">Loading stay categories...</p>
              ) : stayCategories.length ? (
                <select
                  value={stayCategoryId}
                  onChange={(e) => setStayCategoryId(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="">Select stay category</option>
                  {stayCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-amber-700">
                  No stay categories found. Complete the Stay section first.
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-800">
                Format
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setItineraryFormat("daywise")}
                  className={`rounded-xl border p-3 text-left transition ${
                    itineraryFormat === "daywise"
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <i className="fi fi-rr-calendar-lines mb-1 block text-lg" />
                  <p className="text-sm font-medium">Day-wise</p>
                  <p className="text-xs text-gray-500">Day 1, Day 2, etc.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setItineraryFormat("datewise")}
                  className={`rounded-xl border p-3 text-left transition ${
                    itineraryFormat === "datewise"
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <i className="fi fi-rr-calendar-clock mb-1 block text-lg" />
                  <p className="text-sm font-medium">Date-wise</p>
                  <p className="text-xs text-gray-500">Actual travel dates</p>
                </button>
              </div>
            </div>
          </div>

          {itineraryFormat === "datewise" ? (
            <div className="max-w-sm">
              <label className="mb-2 block text-sm font-medium text-gray-800">
                Start date
              </label>
              <input
                type="date"
                value={startDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          ) : null}

          <div className="border-t border-gray-100 pt-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Custom prefix days
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Optional admin-only days before the package itinerary.
                  {customPrefixDays.length > 0 ? (
                    <>
                      {" "}
                      Package days start from{" "}
                      <span className="font-medium text-primary-700">
                        Day {itineraryStartDay}
                      </span>
                      .
                    </>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={addCustomDay}
                className="inline-flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-primary-400 hover:text-primary-700"
              >
                <i className="fi fi-rr-plus-small relative top-0.5" />
                Add day
              </button>
            </div>

            {customPrefixDays.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                No custom days added yet.
              </p>
            ) : (
              <div className="max-h-[45vh] space-y-4 overflow-y-auto pr-1">
                {customPrefixDays.map((day, index) => (
                  <div
                    key={day.id}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-gray-800">
                        Day {index + 1}{" "}
                        <span className="font-normal text-gray-500">(custom)</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => removeCustomDay(day.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mb-4">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Title (optional)
                      </label>
                      <input
                        type="text"
                        value={day.title}
                        onChange={(e) =>
                          updateCustomDay(day.id, "title", e.target.value)
                        }
                        placeholder="e.g. Travel to destination"
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <TinyEditor
                        value={day.description}
                        onChange={(content) =>
                          updateCustomDay(day.id, "description", content)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
      </Dialog>
    </>
  );
};

export default PackageItineraryDownload;
